import { type FormEvent, useId } from "react";

import { CODE_LENGTH } from "./signup.constants.ts";

type Props = {
  email: string;
  code: string;
  resendIn: number;
  error: string | null;
  busy: boolean;
  onChange: (raw: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onBack: () => void;
};

/**
 * The step that makes the account exist.
 *
 * Nothing has been created up to this point — the details have lived in a
 * pending row keyed to this code, and no auth user, no principal and no ONDO
 * id exist until the code checks out. That ordering is what stops a script
 * from minting accounts it can never receive mail for.
 */
export function EmailCodePanel({
  email,
  code,
  resendIn,
  error,
  busy,
  onChange,
  onSubmit,
  onResend,
  onBack,
}: Props) {
  const id = useId();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }

  return (
    <form className="signup__form" onSubmit={submit} noValidate>
      <p className="signup__sent">
        We have sent a {CODE_LENGTH}-digit code to <strong>{email}</strong>.
        Enter it to create your account.
      </p>

      <div className="signup__field">
        <label className="signup__label" htmlFor={`${id}-code`}>
          Verification code
        </label>
        <input
          id={`${id}-code`}
          className="signup__code"
          // `inputMode` rather than type="number": a number input adds spinners,
          // strips leading zeros, and scrolls its value on a trackpad.
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          placeholder="000000"
          value={code}
          disabled={busy}
          autoFocus
          onChange={(event) => onChange(event.target.value)}
        />
      </div>

      {error && (
        <p className="signup__error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="signup__submit" disabled={busy}>
        {busy ? "Creating your account…" : "Verify and create account →"}
      </button>

      <div className="signup__resend">
        {resendIn > 0 ? (
          <span aria-live="polite">
            Didn&rsquo;t get it? You can ask for another in {resendIn}s.
          </span>
        ) : (
          <>
            Didn&rsquo;t get it?{" "}
            <button
              type="button"
              className="signup__link"
              disabled={busy}
              onClick={onResend}
            >
              Send another code
            </button>
          </>
        )}
      </div>

      <p className="signup__footnote">
        Wrong address?{" "}
        <button type="button" className="signup__link" onClick={onBack}>
          Go back and change it
        </button>
      </p>
    </form>
  );
}
